import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Bell, Briefcase, MessageSquare, CheckCircle, X, Check, BellRing } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationBell = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addNotificationHandler, requestNotificationPermission } = useWebSocket();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribePush, permission: pushPermission } = usePushNotifications();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  // Fetch notifications on mount
  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchUnreadCount();
      // Request browser notification permission
      requestNotificationPermission?.();
    }
  }, [user, requestNotificationPermission]);

  // Listen for real-time notifications
  useEffect(() => {
    if (!addNotificationHandler) return;
    
    const removeHandler = addNotificationHandler((notificationData) => {
      // Add new notification to the list
      setNotifications(prev => [{
        id: Date.now().toString(),
        ...notificationData,
        is_read: false,
        created_at: notificationData.timestamp || new Date().toISOString()
      }, ...prev.slice(0, 19)]);
      
      // Increment unread count
      setUnreadCount(prev => prev + 1);
    });

    return () => removeHandler();
  }, [addNotificationHandler]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=20`, { withCredentials: true });
      setNotifications(res.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread/count`, { withCredentials: true });
      setUnreadCount(res.data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_job':
        return <Briefcase className="w-4 h-4 text-[#1E3A8A]" />;
      case 'new_message':
        return <MessageSquare className="w-4 h-4 text-[#F59E0B]" />;
      case 'application_status':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      default:
        return <Bell className="w-4 h-4 text-[#4B5563]" />;
    }
  };

  const getNotificationLink = (notification) => {
    const data = notification.data || {};
    switch (notification.notification_type) {
      case 'new_job':
        return data.job_id ? `/jobs/${data.job_id}` : '/jobs';
      case 'new_message':
        return data.sender_id ? `/dashboard/messages/${data.sender_id}` : '/dashboard/messages';
      case 'application_status':
        return data.job_id ? `/jobs/${data.job_id}` : '/dashboard';
      default:
        return '/dashboard';
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <h3 className="font-heading font-bold">Notifications</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className="text-xs text-[#1E3A8A]"
            >
              <Check className="w-3 h-3 mr-1" />
              Tout marquer lu
            </Button>
          )}
        </div>
        <ScrollArea className="h-[300px]">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <Link 
                key={notification.id} 
                to={getNotificationLink(notification)}
                onClick={() => {
                  if (!notification.is_read) {
                    markAsRead(notification.id);
                  }
                  setOpen(false);
                }}
              >
                <div 
                  className={`px-4 py-3 border-b border-[#1E3A8A]/5 hover:bg-[#F9FAFB] transition-colors ${
                    !notification.is_read ? 'bg-[#1E3A8A]/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#F9FAFB] rounded-full flex items-center justify-center flex-shrink-0">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-[#4B5563] truncate">{notification.body}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">{formatTime(notification.created_at)}</p>
                    </div>
                    {!notification.is_read && (
                      <span className="w-2 h-2 bg-[#F59E0B] rounded-full flex-shrink-0 mt-2"></span>
                    )}
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="p-4 text-center text-[#4B5563]">
              <Bell className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]/30" />
              <p className="text-sm">Aucune notification</p>
            </div>
          )}
        </ScrollArea>
        <DropdownMenuSeparator />
        <div className="p-2 space-y-1">
          {pushSupported && !pushSubscribed && pushPermission !== 'denied' && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-[#F59E0B] gap-1 justify-start"
              onClick={async (e) => { e.stopPropagation(); await subscribePush(); }}
              data-testid="enable-push-notifications"
            >
              <BellRing className="w-4 h-4" />
              Activer les notifications push
            </Button>
          )}
          <Link to="/dashboard" onClick={() => setOpen(false)}>
            <Button variant="ghost" size="sm" className="w-full text-[#1E3A8A]">
              Voir le tableau de bord
            </Button>
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
